import type { ReactNode } from 'react';
import { SwitchCase } from '@toss/react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { ArrowRightIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';
import type { HintData } from '@/api/gen/_model/hintData';
import type { Item } from '@/api/gen/_model/item';
import type { QuizData } from '@/api/gen/_model/quizData';
import { QuizCard, type QuizType } from '@/shared/components/QuizCardV2';
import { QuizEssayAnswer } from '@/shared/components/QuizCardV2/QuizEssayAnswer';
import { prepareEquationHTML, StaticRichTextRenderer } from '@/shared/features/rich-text';
import { sortItemsByOrderIds } from '@/shared/quiz/orderItems';
import { cn } from '@/utils/tailwind.util';
import { deterministicShuffle } from '../utils/deterministicShuffle';
import { AnswerHint, AnswerSection } from './AnswerSection';
import { HiddenAnswerNotice } from './HiddenAnswerNotice';
import { OrderItemList } from './OrderItemList';

/**
 * QuizItemData - QuizDetail 페이지에서 표시할 퀴즈 항목 데이터 타입
 * API의 QuizData를 기반으로 하되, id를 필수로 하고 imageUrl을 optional로 재정의
 */
export type QuizItemData = Omit<QuizData, 'imageUrl'> & {
  id: string;
  imageUrl?: string;
};

/**
 * QuizItemCard - QuizDetail 페이지용 읽기 전용 퀴즈 카드
 * Story 1.1의 QuizCardV2 아토믹 컴포넌트 사용
 *
 * @description
 * 아토믹 QuizCard 컴포넌트를 사용하여 퀴즈 문제를 읽기 전용 형식으로 표시합니다.
 * 세 가지 퀴즈 유형을 모두 지원: 객관식, OX, 주관식.
 *
 * @example
 * <QuizItemCard data={quizData} index={0} />
 */

interface QuizItemCardProps {
  data: QuizItemData;
  index: number;
  isAnswerHidden?: boolean;
  /** 1번 카드 안내의 "정답 보기" 버튼 핸들러 */
  onRevealAnswers?: () => void;
}
export const QuizItemCard = ({
  data,
  index,
  isAnswerHidden = false,
  onRevealAnswers
}: QuizItemCardProps) => {
  const {
    t
  } = useTranslation(['common', 'build']);

  // 공통 데이터 추출
  const questionNumber = `${t('quizItem.question.title')} ${index + 1}`;
  const imageUrl = data.imageUrl;
  const hint = data.hint;
  const notice = index === 0 && isAnswerHidden && onRevealAnswers ? <HiddenAnswerNotice onReveal={onRevealAnswers} /> : null;
  const answerProps = {
    isAnswerHidden,
    notice
  };
  return <QuizCard.Root className="flex-shrink-0" data-sentry-element="unknown" data-sentry-component="QuizItemCard" data-sentry-source-file="QuizItemCard.tsx">
      <QuizCard.Header data-sentry-element="unknown" data-sentry-source-file="QuizItemCard.tsx" />

      <QuizCard.Content data-sentry-element="unknown" data-sentry-source-file="QuizItemCard.tsx">
        <div className="flex items-center gap-sm">
          <span className="text-heading-sm-bold text-default">
            {questionNumber}
          </span>
          <QuizCard.TypeBadge type={data.type as QuizType} data-sentry-element="unknown" data-sentry-source-file="QuizItemCard.tsx" />
        </div>
        <SwitchCase value={data.type} caseBy={{
        multiple: <MultipleChoiceContent data={data} imageUrl={imageUrl} hint={hint} {...answerProps} />,
        ox: <OXChoiceContent data={data} imageUrl={imageUrl} hint={hint} {...answerProps} />,
        subjective: <SubjectiveContent data={data} imageUrl={imageUrl} hint={hint} {...answerProps} />,
        essay: <EssayContent data={data} imageUrl={imageUrl} hint={hint} {...answerProps} />,
        match: <MatchContent data={data} imageUrl={imageUrl} hint={hint} {...answerProps} />,
        order: <OrderContent data={data} imageUrl={imageUrl} hint={hint} {...answerProps} />
      }} data-sentry-element="SwitchCase" data-sentry-source-file="QuizItemCard.tsx" />
      </QuizCard.Content>
    </QuizCard.Root>;
};
interface ContentProps {
  data: QuizItemData;
  imageUrl?: string;
  hint?: HintData;
  isAnswerHidden: boolean;
  notice?: ReactNode;
}

/**
 * MultipleChoiceContent - 객관식 퀴즈 콘텐츠 렌더링
 */
const MultipleChoiceContent = ({
  data,
  imageUrl,
  hint,
  isAnswerHidden,
  notice
}: ContentProps) => {
  const multipleData = data.multipleData;
  if (!multipleData) return null;
  const questionText = multipleData.questionRichText || multipleData.question || '';
  const choices = multipleData.choiceListV2 ?? [];
  const answerList = multipleData.answerList?.map(Number) ?? [];
  return <>
      <QuizCard.QuestionText text={questionText} data-sentry-element="unknown" data-sentry-source-file="QuizItemCard.tsx" />

      {imageUrl && <QuizCard.Image.QuestionImage src={imageUrl} alt="Question image" />}

      <AnswerSection isAnswerHidden={isAnswerHidden} notice={notice} data-sentry-element="AnswerSection" data-sentry-source-file="QuizItemCard.tsx">
        <QuizCard.MultipleChoice.Container data-sentry-element="unknown" data-sentry-source-file="QuizItemCard.tsx">
          {choices.map((choice, idx) => {
          const choiceIndex = idx;
          const isAnswer = !isAnswerHidden && answerList.includes(choiceIndex);
          return <QuizCard.MultipleChoice.Item key={idx} text={choice.text || ''} imageUrl={choice.imageUrl} isAnswer={isAnswer} choiceNumber={choiceIndex} textClassName={isAnswerHidden ? 'text-heading-sm-bold text-assistive' : undefined} />;
        })}
        </QuizCard.MultipleChoice.Container>
      </AnswerSection>

      <AnswerHint hint={hint} isAnswerHidden={isAnswerHidden} data-sentry-element="AnswerHint" data-sentry-source-file="QuizItemCard.tsx" />
    </>;
};

/**
 * OXChoiceContent - O/X 퀴즈 콘텐츠 렌더링
 */
const OXChoiceContent = ({
  data,
  imageUrl,
  hint,
  isAnswerHidden,
  notice
}: ContentProps) => {
  const oxData = data.oxData;
  if (!oxData) return null;
  const questionText = oxData.questionRichText || oxData.question || '';
  const answer = oxData.answer?.toLowerCase() as 'o' | 'x';
  return <>
      <QuizCard.QuestionText text={questionText} data-sentry-element="unknown" data-sentry-source-file="QuizItemCard.tsx" />

      {imageUrl && <QuizCard.Image.QuestionImage src={imageUrl} alt="Question image" />}

      <AnswerSection isAnswerHidden={isAnswerHidden} notice={notice} data-sentry-element="AnswerSection" data-sentry-source-file="QuizItemCard.tsx">
        {!isAnswerHidden && <QuizCard.OXChoice.Container answer={answer} />}
      </AnswerSection>

      <AnswerHint hint={hint} isAnswerHidden={isAnswerHidden} data-sentry-element="AnswerHint" data-sentry-source-file="QuizItemCard.tsx" />
    </>;
};

/**
 * SubjectiveContent - 주관식 퀴즈 콘텐츠 렌더링
 */
const SubjectiveContent = ({
  data,
  imageUrl,
  hint,
  isAnswerHidden,
  notice
}: ContentProps) => {
  const subjectiveData = data.subjectiveData;
  if (!subjectiveData) return null;
  const questionText = subjectiveData.questionRichText || subjectiveData.question || '';
  const answerList = subjectiveData.answerList ?? [];
  return <>
      <QuizCard.QuestionText text={questionText} data-sentry-element="unknown" data-sentry-source-file="QuizItemCard.tsx" />

      {imageUrl && <QuizCard.Image.QuestionImage src={imageUrl} alt="Question image" />}

      <AnswerSection isAnswerHidden={isAnswerHidden} notice={notice} data-sentry-element="AnswerSection" data-sentry-source-file="QuizItemCard.tsx">
        {!isAnswerHidden && <QuizCard.SubjectiveAnswer.Container>
            {answerList.map((answer, idx) => <QuizCard.SubjectiveAnswer.Item key={idx} text={answer} />)}
          </QuizCard.SubjectiveAnswer.Container>}
      </AnswerSection>

      <AnswerHint hint={hint} ignoreSpaces={subjectiveData.ignoreSpaces} isAnswerHidden={isAnswerHidden} data-sentry-element="AnswerHint" data-sentry-source-file="QuizItemCard.tsx" />
    </>;
};
const MatchAnswerPairItem = ({
  leftItem,
  rightItem,
  isAnswerHidden
}: {
  leftItem?: Item;
  rightItem?: Item;
  isAnswerHidden: boolean;
}) => {
  const renderItem = (item?: Item) => {
    const content = item?.richText || (item?.equation ? prepareEquationHTML(item.equation) : item?.text);
    return <div className="flex min-w-[72px] flex-1 flex-col items-start justify-center gap-2xs px-3xs py-2xs text-left" data-sentry-component="renderItem" data-sentry-source-file="QuizItemCard.tsx">
        {item?.imageUrl && <QuizCard.Image.Container className="h-[96px] max-w-full">
            <QuizCard.Image.Thumbnail src={item.imageUrl} alt="" className="max-h-[96px] max-w-full object-contain" />
          </QuizCard.Image.Container>}
        {(content || !item?.imageUrl) && <StaticRichTextRenderer as="div" text={content || '—'} className={cn('min-w-0 break-words text-heading-sm-bold', isAnswerHidden ? 'text-assistive' : 'text-default')} />}
      </div>;
  };
  return <div className="flex min-w-0 flex-wrap items-start gap-x-xs gap-y-[2px]" data-sentry-component="MatchAnswerPairItem" data-sentry-source-file="QuizItemCard.tsx">
      {renderItem(leftItem)}
      <div className="flex min-w-[96px] flex-1 items-start gap-xs">
        {!isAnswerHidden && <div className="flex shrink-0 pb-xs pt-sm">
            <ArrowRightIcon size="xs" fill={ICON_COLOR.assistive} />
          </div>}
        {renderItem(rightItem)}
      </div>
    </div>;
};

/**
 * MatchContent - 매칭형 퀴즈 콘텐츠 렌더링
 * 숨김 상태는 오른쪽 열만 섞는다(PRD 15.2)
 */
const MatchContent = ({
  data,
  imageUrl,
  hint,
  isAnswerHidden,
  notice
}: ContentProps) => {
  const matchData = data.matchData;
  if (!matchData) return null;
  const questionText = matchData.questionRichText || matchData.question || '';
  const leftItemById = new Map(matchData.leftItems.map(item => [item.id, item]));
  const rightItemById = new Map(matchData.rightItems.map(item => [item.id, item]));
  const answerRightIds = matchData.answerPairs.map(pair => pair.rightId);
  const displayRightIds = isAnswerHidden ? deterministicShuffle(answerRightIds, id => id, data.id) : answerRightIds;
  return <>
      <QuizCard.QuestionText text={questionText} data-sentry-element="unknown" data-sentry-source-file="QuizItemCard.tsx" />

      {imageUrl && <QuizCard.Image.QuestionImage src={imageUrl} alt="Question image" />}

      <AnswerSection isAnswerHidden={isAnswerHidden} notice={notice} data-sentry-element="AnswerSection" data-sentry-source-file="QuizItemCard.tsx">
        <div className="flex flex-col gap-2xs pb-xs">
          {matchData.answerPairs.map((pair, rowIndex) => {
          const rightId = displayRightIds[rowIndex];
          return <MatchAnswerPairItem key={`${pair.leftId}-${rightId}`} leftItem={leftItemById.get(pair.leftId)} rightItem={rightItemById.get(rightId)} isAnswerHidden={isAnswerHidden} />;
        })}
        </div>
      </AnswerSection>

      <AnswerHint hint={hint} isAnswerHidden={isAnswerHidden} data-sentry-element="AnswerHint" data-sentry-source-file="QuizItemCard.tsx" />
    </>;
};

/**
 * OrderContent - 순서 맞추기 퀴즈 콘텐츠 렌더링
 * 표시 상태는 정답 순서대로, 숨김 상태는 섞어서 보여준다(PRD 15.2)
 */
const OrderContent = ({
  data,
  imageUrl,
  hint,
  isAnswerHidden,
  notice
}: ContentProps) => {
  const orderData = data.orderData;
  if (!orderData) return null;
  const questionText = orderData.questionRichText || orderData.question || '';
  const orderedItems = sortItemsByOrderIds(orderData.items, orderData.correctOrderIds);
  const displayItems = isAnswerHidden ? deterministicShuffle(orderedItems, item => item.id, data.id) : orderedItems;
  return <>
      <QuizCard.QuestionText text={questionText} data-sentry-element="unknown" data-sentry-source-file="QuizItemCard.tsx" />

      {imageUrl && <QuizCard.Image.QuestionImage src={imageUrl} alt="Question image" />}

      <AnswerSection isAnswerHidden={isAnswerHidden} notice={notice} data-sentry-element="AnswerSection" data-sentry-source-file="QuizItemCard.tsx">
        <OrderItemList items={displayItems} isAnswerHidden={isAnswerHidden} data-sentry-element="OrderItemList" data-sentry-source-file="QuizItemCard.tsx" />
      </AnswerSection>

      <AnswerHint hint={hint} isAnswerHidden={isAnswerHidden} data-sentry-element="AnswerHint" data-sentry-source-file="QuizItemCard.tsx" />
    </>;
};

/**
 * EssayContent - 서술형 퀴즈 콘텐츠 렌더링 (정답 없음)
 */
const EssayContent = ({
  data,
  imageUrl,
  hint,
  isAnswerHidden,
  notice
}: ContentProps) => {
  const essayData = data.essayData;
  if (!essayData) return null;
  const questionText = essayData.questionRichText || essayData.question || '';
  return <>
      <QuizCard.QuestionText text={questionText} data-sentry-element="unknown" data-sentry-source-file="QuizItemCard.tsx" />

      {imageUrl && <QuizCard.Image.QuestionImage src={imageUrl} alt="Question image" />}

      <AnswerSection isAnswerHidden={isAnswerHidden} notice={notice} data-sentry-element="AnswerSection" data-sentry-source-file="QuizItemCard.tsx">
        {!isAnswerHidden && <QuizEssayAnswer.Placeholder />}
      </AnswerSection>

      <AnswerHint hint={hint} isAnswerHidden={isAnswerHidden} data-sentry-element="AnswerHint" data-sentry-source-file="QuizItemCard.tsx" />
    </>;
};